import React, { useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import apiClient from 'apiClient';
import ErrorManager from 'shared/Messages/ErrorManager';
import ApplicationWait from 'shared/ApplicationWait';
import List from 'components/List/List';
import { Row } from 'components/List/types';
import { ClubViewModel, AVAILABLE_ACTIVATIONS, AVAILABLE_STATUSES } from 'store/clubs/types/types';
import { clubRegistrationStep1, clubs as clubsPaths } from 'routes/routePaths';
import { getVerifyStrategyFactoryMethod } from 'utils/request/lists';
import useBodyClass from 'helpers/hooks/useBodyClass';

const changeStatusRequest = (clubId: string, newStatus: boolean): Promise<any> => {
  const verifyStrategy = getVerifyStrategyFactoryMethod('club', 'lmc_status')({ id: clubId }, newStatus);
  return apiClient.post(verifyStrategy.endpoint, verifyStrategy.body);
};

const formatAddress = (item: any): string => {
  const addressParts = [item.house_number, `${item.street_line_1},`, `${item.city},`, item.post_code];

  return addressParts.join(' ');
};

const mapResponseData = (data: any[]): ClubViewModel[] =>
  data.map((item) => ({
    isActive: item.is_active,
    address: formatAddress(item),
    email: item.email,
    id: item.id,
    name: item.name,
  }));

const ClubsList: React.FC<RouteComponentProps> = ({ history }: RouteComponentProps) => {
  const [clubs, setClubs] = useState<ClubViewModel[]>([]);
  const [error, setError] = useState();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);
  useBodyClass('light');

  const toggleActiveStatus = (clubId: string): void => {
    const clubIndex = clubs.findIndex((club) => club.id === clubId);

    const toggleStatus = async () => {
      try {
        if (clubIndex !== -1) {
          const newClubs = [...clubs];
          const newActiveStatus = !newClubs[clubIndex].isActive;
          await changeStatusRequest(clubId, newActiveStatus);

          newClubs[clubIndex].isActive = newActiveStatus;
          setClubs(newClubs);
        }
      } catch (e) {
        throw new Error('Could not change LMC Activation.');
      }
    };

    setIsProcessing(true);
    toggleStatus()
      .catch((e) => {
        setError(e.message);
      })
      .finally(() => {
        setIsProcessing(false);
      });
  };

  useEffect(() => {
    let shouldCancel = false;

    const getData = async () => {
      try {
        const res = await apiClient.get(`/api/v1/broker-management/clubs/`);
        if (!shouldCancel) {
          setClubs(mapResponseData(res.data));
        }
      } catch (err) {
        if (!shouldCancel) setError(err.response);
      }
    };

    getData();

    return () => {
      shouldCancel = true;
    };
  }, []);

  const prepareRows = (items: ClubViewModel[]): Row[] => {
    return items.map((item) => ({
      key: item.id.toString(),
      data: [
        { label: item.name, link: { href: `${clubsPaths.details}/${item.id}` } },
        { label: item.email },
        { label: item.address },
        { label: item.isActive ? AVAILABLE_STATUSES.ACTIVE : AVAILABLE_STATUSES.INACTIVE },
        {
          label: item.isActive ? AVAILABLE_ACTIVATIONS.DEACTIVATE : AVAILABLE_ACTIVATIONS.ACTIVATE,
          action: () => toggleActiveStatus(item.id),
        },
      ],
    }));
  };

  const redirectToClubRegistration = () => history.push(clubRegistrationStep1);

  return (
    <>
      {isProcessing && <ApplicationWait />}
      <List
        labels={['Club Name', 'Email', 'Address', 'LMC Status', 'LMC Activation']}
        rows={prepareRows(clubs)}
        listName="Clubs"
        onAction={toggleActiveStatus}
        addNewLabel="Add Club"
        onAddNewItem={redirectToClubRegistration}
      />
      {error && <ErrorManager error={error} />}
    </>
  );
};

export default ClubsList;
